import { Middleware } from './middleware';
import { AuthenticationModule } from 'src/modules/authentication_module';
import { InteractionModule } from 'src/modules/interaction_module';
import { NetworkModule } from '../modules/network_module';
import { SessionModule } from '../modules/session_module';
import { NavigationModule } from 'src/modules/navigation_module';
import { ProductModule } from 'src/modules/product_module';
import { LoggingModule } from 'src/modules/logging_module';
import { HistoryModule } from 'src/modules/history_module';
import { LoadingModule } from 'src/modules/loading_module';
import { ViewModule, t_environment } from 'src/modules/view_module';
import { MenuModule } from '../modules/menu_module';
import { SentryModule } from '../modules/sentry_module';
import { AnalyticsModule } from '../modules/analytics_module';
import { PrimaryMenuViewRC } from 'src/support/menu/primary_menu_view_rc';
import { TertiaryMenuViewRC } from 'src/support/menu/tertiary_menu_view_rc';
import { SecondaryMenuViewRC } from 'src/support/menu/secondary_menu_view_rc';
import { HierarchyMenuViewRC } from 'src/support/menu/hierarchy_menu_view_rc';
import { IAHPConfiguration, AHPConfiguration, t_ahp_configuration } from 'src/core/configuration';
import { IModuleConfiguration } from 'src/types/system_types';
import { version, getActiveRoute } from './settings';

import ess from'../../../pip/library/pi-ess/pi-ess.js';
import tt from'../../../pip/library/pi-tt/pi-tt.js';

export class Hosting {
  static readonly version = version;

  readonly configuration: AHPConfiguration = new AHPConfiguration(this);

  readonly authentication = new AuthenticationModule(this);
  readonly interaction = new InteractionModule(this);
  readonly network = new NetworkModule(this);
  readonly session = new SessionModule(this);
  readonly navigation = new NavigationModule(this);
  readonly product = new ProductModule(this);
  readonly logging = new LoggingModule(this);
  readonly menu = new MenuModule(this);
  readonly view = new ViewModule(this);
  readonly sentry = new SentryModule(this);
  readonly history = new HistoryModule(this);
  readonly loading = new LoadingModule(this);
  readonly analytics = new AnalyticsModule(this);

  readonly views = {
    menus: {
      hierarchy: new HierarchyMenuViewRC(this),
      secondary: new SecondaryMenuViewRC(this),
      tertiary: new TertiaryMenuViewRC(this),
      primary: new PrimaryMenuViewRC(this)
    }
  };

  readonly systemNames = [
    'analytics',
    'authentication',
    'interaction',
    'network',
    'session',
    'logging',
    'view',
    // CRITICAL this depends on view updating the tenant (if needed) before we call the tenant
    'product',
    // CRITICAL this has to be second last because the menu relies on the product configuration
    'menu',
    // CRITICAL this has to be last because we activate the router on the `activate` step
    'navigation',
    // CRITICAL this depends on view for constants of environment and release
    'sentry',
    // currently putting in last not sure about the order
    'history',
    // currently puttting in last not sure about the order
    'loading'
  ];

  get getActiveRoute() {
    return getActiveRoute();
  }

  /**
   * load configuration based for an environment and initialize ahp
   */
  configure(environment: t_environment = 'local', paths = [`config/common.json`, `config/@environment.json`]) {
    const ahp = (window['ahp'] = this);
    const configFetches = tt.map((path) => {
      return fetch(path.replace('@environment', environment)).then((response) => response.json());
    }, paths);

    return Promise.all(configFetches).then((configurations: Record<string, any>[]) => {
      const allConfig = tt.mergeWith.apply(tt, ['clobber', {}, ...configurations]);

      const envConfig = tt.mergeWith('clobber', allConfig, {
        view: {
          system: {
            environment
          }
        }
      });

      return ahp.initialize((envConfig as unknown) as IAHPConfiguration);
    });
  }

  /**
   *
   * @param configuration
   */
  initialize(c: IAHPConfiguration) {
    // TODO apply HIL.configuration to apply constants on values of the configuration
    this.configuration.write(c);

    const activeModules = this.__deriveActiveModules();

    return Promise.resolve({})
      .then(() => this.__logActivateModules(activeModules))
      .then(() => this.__initializeModules(activeModules))
      .then(() => this.__applyMiddleware(activeModules))
      .then(() => this.__activateModules(activeModules))
      .then(() => this);
  }

  __deriveActiveModules() {
    return tt.filter((systemName: t_ahp_configuration) => {
      return this.configuration._[systemName].disabled !== true;
    }, this.systemNames);
  }

  __logActivateModules(activeModules: string[]) {
    return tt.each((site: t_ahp_configuration) => {
      ess.success({
        action: 'enable',
        ns: 'ahp',
        site
      });
    }, activeModules);
  }

  __initializeModules(activeModules: string[]) {
    return Promise.all(
      tt.map((moduleName: t_ahp_configuration) => {
        return this[moduleName]
          .initialize(this.configuration._[moduleName] as any, this.configuration._)
          .then((conf: IModuleConfiguration) => {
            this.configuration.assign(moduleName, conf);
            return conf;
          })
          .then((conf: IModuleConfiguration) => {
            ess.success({
              ns: 'ahp',
              site: moduleName,
              action: 'initialize',
              data: conf
            });
          })
          .catch((error: Error) => {
            ess.failure({
              ns: 'ahp',
              site: moduleName,
              action: 'initialize',
              data: {
                error
              }
            });
          });
      }, activeModules)
    );
  }

  __applyMiddleware(activeModules: string[]) {
    const middleware = new Middleware();

    return middleware.run(this.configuration._).then((_configuration) => {
      this.configuration.write(_configuration).disseminateToModules(activeModules);
    });
  }

  __activateModules(activeModules: string[]) {
    const conf = this.configuration._ as any;

    return Promise.all(
      tt.map((moduleName: t_ahp_configuration) => {
        return this[moduleName]
          .activate(conf)
          .then(() => {
            ess.success({
              ns: 'ahp',
              site: moduleName,
              action: 'activate'
            });
          })
          .catch((error: Error) => {
            ess.failure({
              ns: 'ahp',
              site: moduleName,
              action: 'activate',
              data: {
                error
              }
            });
          });
      }, activeModules)
    );
  }

  /**
   *
   * @param configuration
   */
  uninitialize() {
    tt.each((systemName: string) => {
      if (this.configuration._[systemName].disabled !== true) {
        this[systemName].uninitialize(this.configuration._[systemName] as any);
      }
    }, this.systemNames);

    this.configuration.clear();
    return this;
  }
}
